// The human check (Cloudflare Turnstile) a phone code needs first. Shown only when the server asks for it
// (human_check_required, with the site key), before anything is looked up about the number. The widget is the
// kit's one Turnstile core (@twoshot/ui-kit/turnstile), loaded on demand, so nobody else downloads it.
import {useEffect, useRef, useState, type ReactNode} from "react";
import {renderTurnstile} from "@twoshot/ui-kit/turnstile";
import {ApiError} from "./api";
import {PHONE_HUMAN_CHECK_ACTION} from "../../shared/deployment-config.ts";

/** The site key when `err` is the server asking for a human check, else null. */
function humanCheckSiteKey(err: unknown): string | null {
    if (!(err instanceof ApiError) || (err.code !== "human_check_required" && err.code !== "human_check_failed")) return null;
    const key = err.detail && typeof err.detail === "object" ? (err.detail as {site_key?: unknown}).site_key : undefined;
    return typeof key === "string" && key ? key : null;
}

type Waiter = {resolve(token: string): void; reject(err: unknown): void};

/** guard(send): runs send(); if the server asks for the check, shows it (slot) and sends once more with
 *  its token. A second refusal, or a check that cannot load, is thrown for the caller's copy. */
export function useHumanCheck(): {guard<T>(send: (token?: string) => Promise<T>): Promise<T>; slot: ReactNode} {
    const [siteKey, setSiteKey] = useState<string | null>(null);
    const box = useRef<HTMLDivElement | null>(null);
    const waiter = useRef<Waiter | null>(null);
    useEffect(() => {
        if (!siteKey || !box.current) return;
        const settle = (fn: (w: Waiter) => void) => { const w = waiter.current; waiter.current = null; if (w) fn(w); };
        const check = renderTurnstile(box.current, {sitekey: siteKey, theme: "auto", size: "flexible", action: PHONE_HUMAN_CHECK_ACTION,
            onToken: token => settle(w => w.resolve(token)),
            onFail: () => settle(w => w.reject(new ApiError(0, "human_check_unavailable")))});
        return () => check.remove();
    }, [siteKey]);

    async function guard<T>(send: (token?: string) => Promise<T>): Promise<T> {
        try { return await send(); }
        catch (err) {
            const key = humanCheckSiteKey(err);
            if (!key) throw err;
            try {
                const token = await new Promise<string>((resolve, reject) => { waiter.current = {resolve, reject}; setSiteKey(key); });
                return await send(token);
            } finally { setSiteKey(null); }
        }
    }
    return {guard, slot: siteKey ? <div className="human-check" ref={box}/> : null};
}
